import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  KeyRound,
  Copy,
  Star,
  Receipt,
  Building2,
  Eye,
  Users,
  Trash2,
  Plus,
  Mail,
  Send,
  Loader2,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import Modal from "../components/Modal";
import { useAuth } from "../context/AuthContext";
import { EntityShell } from "../components/shell";
import { usePersistedState } from "../lib/persisted";
import { pushRecent } from "../lib/recents";

import CustomerHeader from "../components/customer-blocks/CustomerHeader";
import CustomerAnnouncementsSection from "../components/customer-blocks/CustomerAnnouncementsSection";
import SitesBlock from "../components/customer-blocks/SitesBlock";
import ContactsBlock from "../components/customer-blocks/ContactsBlock";
import JobCardsBlock from "../components/customer-blocks/JobCardsBlock";
import TicketsBlock from "../components/customer-blocks/TicketsBlock";
import PickingListsBlock from "../components/customer-blocks/PickingListsBlock";
import LeadsBlock from "../components/customer-blocks/LeadsBlock";
import CallsBlock from "../components/customer-blocks/CallsBlock";
import EquipmentBlock from "../components/customer-blocks/EquipmentBlock";
import SiteEquipmentPicker from "../components/customer-blocks/SiteEquipmentPicker";
import AccountManagementBlock from "../components/customer-blocks/AccountManagementBlock";
import DocumentsBlock from "../components/customer-blocks/DocumentsBlock";
import CustomerProfileDocumentsBlock from "../components/customer-blocks/CustomerProfileDocumentsBlock";
import GalleryBlock from "../components/customer-blocks/GalleryBlock";
import OrdersBlock from "../components/customer-blocks/OrdersBlock";
import ConversationsBlock from "../components/customer-blocks/ConversationsBlock";
import MeetingsBlock from "../components/customer-blocks/MeetingsBlock";
import CustomerActivityBlock from "../components/customer-blocks/CustomerActivityBlock";
import LogActivityModal from "../components/LogActivityModal";
import PerformanceBlock from "../components/perf/PerformanceBlock";
import PlantUptimeBlock from "../components/perf/PlantUptimeBlock";
import PerfEditRequestsInbox from "../components/perf/PerfEditRequestsInbox";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  // [Iter 290zp.22] Was: `["admin","manager","owner"].includes(user?.role)`
  // — a hardcoded role check that ignored the `action.edit_customers`
  // permission entirely. Reps have edit_customers=True by default in the
  // role preset but were locked out by this check. Owner can still revoke
  // per-user via Settings → Users & Roles → Permissions matrix.
  const canEdit = hasPermission("action.edit_customers");
  // Technicians get a hard-locked view: only Work (Jobs + Tickets) and
  // Media (Gallery). All other tabs are hidden, all "other" customer-detail
  // endpoints are skipped (saves ~9 round-trips per page load — fixes the
  // "super slow on tech" report).
  const isTech = user?.role === "technician";

  const [activeTab, setActiveTab] = usePersistedState("customer.activeTab", isTech ? "work" : "overview");
  // Iter 400.2 — Techs now have access to Overview / Equipment /
  // Performance in addition to Work + Media (dirk request 2026-02-19:
  // "technicians need to see contact details, equipment, performance,
  // job quotes and support tickets"). We still hide Account /
  // Documents / Orders / Events / Conversations / Call-logs — those
  // stay owner/manager only. Force-route if the persisted tab is
  // outside the tech whitelist.
  useEffect(() => {
    const TECH_ALLOWED = ["overview", "work", "media", "equipment", "performance"];
    if (isTech && !TECH_ALLOWED.includes(activeTab)) {
      setActiveTab("work");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTech]);

  // Iter 369 — allow deep-linking to a specific tab via ?tab=<key>
  // Used by the Performance block's "Open Sites tab" CTA.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    if (t) setActiveTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [c, setC] = useState(null);
  const [sites, setSites] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [leads, setLeads] = useState([]);
  const [calls, setCalls] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [serviceLogs, setServiceLogs] = useState([]);
  const [salesDocs, setSalesDocs] = useState([]);
  const [galleryFolders, setGalleryFolders] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [chatMedia, setChatMedia] = useState([]);
  // Iter 400.2 — picking lists shown in the Work tab so techs can
  // drill into a picking slip straight from the customer page.
  const [pickingLists, setPickingLists] = useState([]);
  const [logActivityOpen, setLogActivityOpen] = useState(false);
  // [Iter 290zp.26] Trip debriefs and meeting outcomes write to the
  // `customer_activity` collection. Surface them here alongside calls
  // + meetings so the rep can see the debrief summaries they committed.
  const [activityEvents, setActivityEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // [Iter 288b] Track 404 / load-failure separately from `loading || !c`.
  // Without this, a 404 on /customers/{id} flips loading=false but c stays
  // null, and the page renders the "Loading customer…" placeholder forever
  // — which is exactly what the rep on production saw when they navigated
  // to a customer ID that doesn't exist in their tenant.
  const [notFound, setNotFound] = useState(false);

  // Iter 290zp.13z21 — Differentiate true 404 from transient errors.
  // The old `notFound` boolean was set on ANY error (502/504/network blip),
  // and the render also nuked the customer from recents. Result: a
  // Cloudflare burp = "Customer not found" + recents wiped.
  const [transientLoadError, setTransientLoadError] = useState(null);
  const transientRetryCountRef = useRef(0);

  const load = useCallback(async () => {
    setNotFound(false);
    setTransientLoadError(null);
    // [Iter 288] Progressive load — was: Promise.allSettled across 12
    // endpoints, then setLoading(false) after the whole batch settled.
    // On a slow prod network, ONE slow endpoint (e.g. /leads pulling
    // 400 KB ungated, or a stalled /jobcards aggregation) keeps the
    // user staring at "Loading customer…" for up to 60s while every
    // other endpoint has already responded with the data needed to
    // render the page.
    //
    // Fix: fetch the customer doc FIRST. As soon as that arrives,
    // flip setLoading(false) and render the page shell. The 11 other
    // fetches run in parallel from there; each setter fires as its
    // own response lands. Tabs that don't have their data yet just
    // show empty/loading inside the tab — non-blocking.
    //
    // Technicians only see Work + Media tabs, so skip the 7 endpoints
    // that feed the hidden tabs entirely (sites, contacts, leads,
    // calls, equipment, service-logs, sales-documents).
    let cust = null;
    try {
      const { data } = await api.get(`/customers/${id}`);
      cust = data;
      transientRetryCountRef.current = 0;
    } catch (e) {
      const status = e?.response?.status;
      // Only 404 (and 410 = gone) are TRUE "not found". Everything else
      // (502/503/504 from Cloudflare while backend is event-loop-blocked,
      // 408 timeout, network errors) is TRANSIENT — show a banner and
      // auto-retry; DO NOT wipe recents.
      const isTrueNotFound = status === 404 || status === 410;
      console.warn(`[CustomerDetail] /customers/${id} failed`, status, e?.message);
      if (isTrueNotFound) {
        setLoading(false);
        setNotFound(true);
        return;
      }
      // Transient: show the banner, auto-retry up to 3 times with backoff.
      setLoading(false);
      setTransientLoadError(
        `${status || "Network"} · ${e?.message || "connection issue"}`,
      );
      const attempt = transientRetryCountRef.current;
      if (attempt < 3) {
        transientRetryCountRef.current = attempt + 1;
        const backoff = 4000 + attempt * 4000;  // 4s, 8s, 12s
        setTimeout(() => { load(); }, backoff);
      }
      return;
    }
    if (!cust) { setLoading(false); setNotFound(true); return; }
    setC(cust);
    pushRecent({
      kind: "customer", id: cust.id, label: cust.name,
      sub: cust.external_ref || "",
    });
    setLoading(false);  // ← Page renders NOW. The rest streams in.

    // Helper: fetch + set, swallow per-endpoint errors so one slow/dead
    // endpoint can't break the tabs that DID load.
    const stream = (url, setter, postProcess = (d) => (Array.isArray(d) ? d : [])) =>
      api.get(url)
        .then(({ data }) => setter(postProcess(data)))
        .catch(() => { /* leave existing state — tab shows empty */ });

    if (isTech) {
      // Iter 400.2 — techs now see Overview (contacts), Equipment,
      // Performance and Work + Media. NEVER sales documents / quotes /
      // account / conversations / leads / calls — those stay owner
      // / manager only per Dirk (2026-02-19). Picking lists ARE
      // visible so they can execute picking from the customer page.
      stream(`/sites?customer_id=${id}`, setSites);
      stream(`/contacts?customer_id=${id}`, setContacts);
      stream(`/equipment?customer_id=${id}`, setEquipment);
      stream(`/service-logs?customer_id=${id}`, setServiceLogs);
      stream(`/jobcards?customer_id=${id}`, setJobs);
      stream(`/tickets?customer_id=${id}`, setTickets);
      stream(`/picking/lists?customer_id=${id}`, setPickingLists);
      stream(`/gallery-folders?customer_id=${id}`, setGalleryFolders);
      stream(`/gallery-items?customer_id=${id}`, setGalleryItems);
      stream(`/customers/${id}/chat-media`, setChatMedia);
      // Iter 400.8 — techs need the Timeline too. Fetch customer_activity
      // so they see every call/whatsapp/email/note logged by anyone.
      stream(`/customers/${id}/activity`, setActivityEvents);
    } else {
      stream(`/sites?customer_id=${id}`, setSites);
      stream(`/contacts?customer_id=${id}`, setContacts);
      stream(`/jobcards?customer_id=${id}`, setJobs);
      stream(`/tickets?customer_id=${id}`, setTickets);
      stream(`/picking/lists?customer_id=${id}`, setPickingLists);
      stream(`/leads?customer_id=${id}`, setLeads);
      stream(`/calls?customer_id=${id}`, setCalls);
      stream(`/equipment?customer_id=${id}`, setEquipment);
      stream(`/service-logs?customer_id=${id}`, setServiceLogs);
      stream(`/sales-documents?customer_id=${id}`, setSalesDocs);
      stream(`/gallery-folders?customer_id=${id}`, setGalleryFolders);
      stream(`/gallery-items?customer_id=${id}`, setGalleryItems);
      stream(`/customers/${id}/chat-media`, setChatMedia);
      // [Iter 290zp.26] Trip debriefs / meeting outcomes.
      stream(`/customers/${id}/activity`, setActivityEvents);
    }
  }, [id, isTech]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);
  // Auto-refresh while the tab is foreground (Wave 19 — concurrent-edit safety).
  useAutoRefresh(load, { intervalMs: 45_000 });

  // Listen for global "calls:logged" so the floating Quick-Log refreshes the calls block
  useEffect(() => {
    const refreshCalls = () => {
      api.get(`/calls?customer_id=${id}`).then(({ data }) =>
        setCalls(Array.isArray(data) ? data : []),
      );
    };
    const refreshGallery = () => {
      Promise.all([
        api.get(`/gallery-folders?customer_id=${id}`),
        api.get(`/gallery-items?customer_id=${id}`),
        api.get(`/customers/${id}/chat-media`),
      ]).then(([gf, gi, cm]) => {
        setGalleryFolders(Array.isArray(gf.data) ? gf.data : []);
        setGalleryItems(Array.isArray(gi.data) ? gi.data : []);
        setChatMedia(Array.isArray(cm.data) ? cm.data : []);
      });
    };
    window.addEventListener("calls:logged", refreshCalls);
    window.addEventListener("gallery:refresh", refreshGallery);
    // Iter 234 — `OcrProcessingStrip` polls /sales-documents and fires
    // this event whenever the next OCR tick lands so the parent state
    // picks up filled-in titles/amounts without a full page reload.
    const onSalesDocsRefresh = (e) => {
      const next = e?.detail?.docs;
      if (Array.isArray(next)) {
        setSalesDocs(next);
      } else {
        api.get(`/sales-documents?customer_id=${id}`).then(({ data }) =>
          setSalesDocs(Array.isArray(data) ? data : []),
        );
      }
    };
    window.addEventListener("salesDocs:refresh", onSalesDocsRefresh);
    return () => {
      window.removeEventListener("calls:logged", refreshCalls);
      window.removeEventListener("gallery:refresh", refreshGallery);
      window.removeEventListener("salesDocs:refresh", onSalesDocsRefresh);
    };
  }, [id]);

  if (loading) {
    return <div className="text-[#647087]" data-testid="customer-detail-loading">Loading customer…</div>;
  }
  // Iter 290zp.13z21 — Transient (5xx/network) error → small banner +
  // auto-retry, do NOT wipe recents, do NOT show the "Customer not
  // found" gravestone.
  if (transientLoadError && !c) {
    return (
      <div
        className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-amber-100"
        data-testid="customer-detail-transient-error"
      >
        <div className="text-sm font-semibold mb-1">Couldn't reach the server</div>
        <div className="text-xs text-amber-200/80 leading-snug">
          A network or backend issue prevented this customer from loading.
          We'll retry automatically. Your recent items are preserved.
        </div>
        <div className="text-[10px] text-amber-200/60 mt-2 font-mono break-all">
          {transientLoadError}
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            type="button"
            onClick={() => {
              transientRetryCountRef.current = 0;
              setTransientLoadError(null);
              setLoading(true);
              load();
            }}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="customer-detail-transient-retry"
          >
            Retry now
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="customer-detail-transient-back"
          >
            Back
          </button>
        </div>
      </div>
    );
  }
  if (notFound || !c) {
    // [Iter 288b] Auto-clean the stale recent entry that probably got
    // them here in the first place — keeps the recents widget honest.
    try {
      const KEY = "hv_recents_v1";
      const raw = localStorage.getItem(KEY);
      if (raw) {
        const arr = JSON.parse(raw).filter((x) => !(x.kind === "customer" && x.id === id));
        localStorage.setItem(KEY, JSON.stringify(arr));
        window.dispatchEvent(new Event("hv:recents:update"));
      }
    } catch { /* ignore */ }
    return (
      <div
        className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-5 text-amber-100"
        data-testid="customer-detail-not-found"
      >
        <div className="text-sm font-semibold mb-1">Customer not found</div>
        <div className="text-xs text-amber-200/80 leading-snug">
          We couldn't load this customer in your tenant. The link may be stale,
          the record may have been deleted, or you may not have access to it.
          We've cleared it from your recent items.
        </div>
        <div className="text-[10px] text-amber-200/60 mt-2 font-mono break-all">
          id: {id}
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            type="button"
            onClick={() => navigate("/customers")}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="customer-detail-not-found-back"
          >
            Back to customers
          </button>
          <button
            type="button"
            onClick={() => { setNotFound(false); setLoading(true); load(); }}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="customer-detail-not-found-retry"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Phase 7 Sub-iter 7B — EntityShell migration.
  //
  // Build the tabs array, respecting role/permission gates.  Keys must
  // stay identical to the legacy `<Tabs>` values so persisted tab state
  // ("customer.activeTab") keeps working across the upgrade.
  const tabs = [
    { key: "overview", label: "Overview" },
    hasPermission("nav.jobcards") && {
      key: "work",
      label: "Work",
      count: (jobs.length + tickets.length + pickingLists.length) || undefined,
    },
    !isTech && { key: "account", label: "Account", count: salesDocs.length || undefined },
    !isTech && { key: "documents", label: "Documents" },
    !isTech && { key: "orders", label: "Orders" },
    { key: "media", label: "Media", count: galleryItems.length || undefined },
    // Iter 400.2 — Equipment & Performance now visible to techs. The
    // sidebar-nav still respects `nav.equipment` / `nav.schedule` so
    // this doesn't add new sidebar entries — just makes the per-customer
    // tab reachable so techs can pull up equipment lists and daily-log
    // performance on the customer they're currently working on.
    (isTech || hasPermission("nav.schedule") || hasPermission("nav.equipment")) && {
      key: "equipment",
      label: "Equipment",
      count: equipment.length || undefined,
    },
    (isTech || hasPermission("nav.customers")) && {
      key: "performance",
      label: "Performance",
    },
    // Iter 400.8 — Timeline unification. Renamed "Call logs" tab to
    // "Timeline"; visible to anyone who can see jobcards (so techs get
    // it too). Broadened count to include everything on the feed.
    hasPermission("nav.jobcards") && {
      key: "activity",
      label: "Timeline",
      count: ((activityEvents || []).length + (calls || []).length) || undefined,
    },
    // Iter 418.bi (Dirk, 2026-02-28) — Full announcement manager tab.
    // Replaces the header FAB. Owner/manager only — techs don't post.
    !isTech && { key: "announcements", label: "Announcements" },
    !isTech && { key: "conversations", label: "Conversations" },
  ].filter(Boolean);

  const statusTone = c.status === "active" ? "green"
    : c.status === "lead" ? "amber"
    : c.status === "archived" ? "slate"
    : "blue";

  return (
    <EntityShell
      shortId={c.external_ref ? `#${c.external_ref}` : undefined}
      kindLabel="Customer"
      title={c.name || "Customer"}
      status={c.status ? { label: c.status, tone: statusTone } : undefined}
      backTo="/customers"
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      footerMeta={{
        createdByName: c.created_by_name,
        createdAtDisplay: c.created_at_display
          || (c.created_at ? new Date(c.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : ""),
        updatedByName: c.updated_by_name,
        updatedAtDisplay: c.updated_at_display
          || (c.updated_at ? new Date(c.updated_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : ""),
      }}
    >
    <div className="space-y-4 md:space-y-5" data-testid="customer-detail">

      {/* Editable header — Iter 383: only visible on Overview tab.
          On Work / Account / Documents / Orders / Performance etc. the
          tab body starts immediately so users have more real estate for
          the task at hand. */}
      {activeTab === "overview" && (
        <CustomerHeader
          customer={c}
          canEdit={canEdit}
          onChanged={load}
          onDeleted={() => navigate("/customers")}
          rightActions={
            canEdit && (
              <>
                <PortalPreviewButton customerId={c.id} customerName={c.name} />
                <PortalInviteButton customer={c} onChanged={load} />
                {/* Iter 418.w — customer-facing email safety-net toggle.
                    DEFAULT OFF for every customer; admin flips ON to
                    enable JC / quote / SO / invoice / credit / statement
                    / document emails via SendGrid. Sits next to the
                    portal-user pill so the two comms channels live
                    together. */}
                <EmailNotificationsToggle customer={c} onChanged={load} />
                {/* Iter 418.at (Dirk, 2026-02-28) — Performance tab is
                    now visible on every customer; the on/off header
                    button was removed to keep the header lean. */}
                {/* Iter 418.bi (Dirk, 2026-02-28) — Removed the
                    "Announcement" header FAB; the composer + history
                    now live in the dedicated "Announcements" tab so
                    editing / who-acked / archiving all sit in one
                    place instead of a modal that vanished on close. */}
              </>
            )
          }
        />
      )}

      {/* Phase 7 Sub-iter 7B — EntityShell drives the tab strip in the
          header now.  Bodies below are conditionally rendered on the
          shell's `activeTab` callback. */}

        {activeTab === "overview" && (
        <div className="space-y-3 mt-3" data-testid="customer-tab-panel-overview">
          {/* Google rating card */}
          {(c.google_rating || c.google_place_id) && (
            <div className="hv-card p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-md bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
                  <Star className="w-4 h-4 text-amber-300 fill-amber-300" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">
                    Google rating
                  </div>
                  {c.google_rating ? (
                    <div className="font-display text-xl font-bold">
                      {c.google_rating}
                      <span className="text-xs font-normal text-[#a0aabf] ml-1">
                        / 5 · {c.google_reviews_count || 0} reviews
                      </span>
                    </div>
                  ) : (
                    <div className="text-xs text-[#647087]">No rating set yet.</div>
                  )}
                </div>
              </div>
              {c.google_place_id && (
                <a
                  href={`https://search.google.com/local/reviews?placeid=${c.google_place_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hv-btn hv-btn-ghost text-xs"
                  data-testid="customer-reviews-link"
                >
                  View reviews
                </a>
              )}
            </div>
          )}
          <SitesBlock customerId={id} sites={sites} canEdit={canEdit} onChanged={load} defaultOpen />
          <ContactsBlock
            customerId={id}
            contacts={contacts}
            canEdit={canEdit}
            onChanged={load}
            defaultOpen
          />
          {/* Iter 400.2 — Leads is a sales/pipeline block; techs
              shouldn't see prospect deals when they open a customer. */}
          {!isTech && <LeadsBlock leads={leads} canEdit={canEdit} onChanged={load} />}
        </div>
        )}

        {activeTab === "work" && (
        <div className="space-y-3 mt-3" data-testid="customer-tab-panel-work">
          <JobCardsBlock jobs={jobs} sites={sites} canEdit={canEdit} onChanged={load} previewEditMode={canEdit} customerId={c?.id} customerName={c?.name} />
          <TicketsBlock tickets={tickets} canEdit={canEdit} onChanged={load} previewEditMode={canEdit} />
          <PickingListsBlock lists={pickingLists} />
        </div>
        )}

        {activeTab === "account" && (
        <div className="space-y-3 mt-3" data-testid="customer-tab-panel-account">
          {/* Company / billing details */}
          {(c.customer_type || "individual") === "company" &&
            (c.billing_address || c.billing_city || c.billing_country) && (
              <div className="hv-card p-4" data-testid="company-details">
                <div className="text-xs uppercase tracking-[0.2em] text-[#647087] flex items-center gap-2 mb-2">
                  <Building2 className="w-3 h-3" /> Company / billing
                </div>
                <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-xs text-[#dde3ec]">
                  {c.vat_number && (
                    <div>
                      VAT: <span className="font-mono">{c.vat_number}</span>
                    </div>
                  )}
                  {c.registration_number && (
                    <div>
                      Reg: <span className="font-mono">{c.registration_number}</span>
                    </div>
                  )}
                  {c.industry && <div>Industry: {c.industry}</div>}
                  {c.website && (
                    <div>
                      Web:{" "}
                      <a
                        href={c.website.startsWith("http") ? c.website : `https://${c.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#1fb6c1] hover:underline"
                      >
                        {c.website}
                      </a>
                    </div>
                  )}
                  {(c.billing_address || c.billing_city) && (
                    <div className="md:col-span-2 mt-1 flex items-start gap-1 text-[#a0aabf]">
                      <Receipt className="w-3 h-3 mt-0.5" />
                      <span>
                        {[c.billing_address, c.billing_city, c.billing_postal_code, c.billing_country]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          <AccountManagementBlock customer={c} canEdit={canEdit} onChanged={load} />
          <DocumentsBlock customer={c} canEdit={canEdit} salesDocs={salesDocs} onChanged={load} />
        </div>
        )}

        {activeTab === "documents" && (
        <div className="space-y-3 mt-3" data-testid="customer-tab-panel-documents">
          <CustomerProfileDocumentsBlock
            customerId={c.id}
            apiBase={`/customers/${c.id}`}
            canEdit={canEdit}
            canDelete={user?.role === "owner" || user?.role === "admin" || user?.role === "manager"}
          />
        </div>
        )}

        {activeTab === "orders" && (
        <div className="space-y-3 mt-3" data-testid="customer-tab-panel-orders">
          <OrdersBlock customerId={c.id} defaultOpen />
        </div>
        )}

        {activeTab === "media" && (
        <div className="space-y-3 mt-3" data-testid="customer-tab-panel-media">
          {(() => {
            const isTech = user?.role === "technician";
            // Restrict techs to media tied to jobs/tickets they're on
            const visibleJobs = isTech
              ? jobs.filter(
                  (j) =>
                    j.technician_id === user?.id ||
                    (j.staff_ids || []).includes(user?.id),
                )
              : jobs;
            const visibleTickets = isTech
              ? tickets.filter(
                  (t) =>
                    t.assigned_to_id === user?.id ||
                    t.created_by_id === user?.id,
                )
              : tickets;
            return (
              <GalleryBlock
                customerId={id}
                folders={galleryFolders}
                items={galleryItems}
                jobcards={visibleJobs}
                tickets={visibleTickets}
                chats={chatMedia}
                canEdit={canEdit || isTech}
                canUpload={canEdit || isTech}
                canDelete={canEdit}
                filterToWorkOnly={isTech}
                onChanged={load}
              />
            );
          })()}
        </div>
        )}

        {activeTab === "equipment" && (
        <div className="space-y-3 mt-3" data-testid="customer-tab-panel-equipment">
          {/* Iter 397e — enforce Customer → Site → Equipment hierarchy.
              Show a site picker instead of a flat equipment list. Each
              site card links to the site's own Equipment tab where the
              user can add/edit equipment, view service intervals,
              schedule + history. */}
          <SiteEquipmentPicker
            sites={sites}
            equipment={equipment}
            serviceLogs={serviceLogs}
            customerId={id}
          />
        </div>
        )}

        {activeTab === "performance" && (
        <div className="space-y-3 mt-3" data-testid="customer-tab-panel-performance">
          {/* Iter 400.21 — Sub-tabs to mirror the top-level Performance
              page (Logbook / Analytics / Plant Uptime / Approvals).
              Approvals here are scoped to THIS customer so the owner
              can approve edit requests without leaving the profile. */}
          <CustomerPerformanceTabs customerId={id} canEdit={canEdit} />
        </div>
        )}

        {activeTab === "activity" && (
        <div className="space-y-3 mt-3" data-testid="customer-tab-panel-activity">
          <MeetingsBlock customerId={id} defaultOpen />
          <CustomerActivityBlock events={activityEvents} onLogClick={() => setLogActivityOpen(true)} />
          <CallsBlock
            customerId={id}
            calls={calls}
            contacts={contacts}
            leads={leads}
            jobcards={jobs}
            tickets={tickets}
            onChanged={load}
          />
        </div>
        )}

        {activeTab === "conversations" && (
        <div className="space-y-3 mt-3" data-testid="customer-tab-panel-conversations">
          <ConversationsBlock customerId={id} customerName={c.name} />
        </div>
        )}

        {/* Iter 418.bi (Dirk, 2026-02-28) — Announcements tab body:
            composer + full history with edit / who-acked / archive. */}
        {activeTab === "announcements" && (
        <div className="space-y-3 mt-3" data-testid="customer-tab-panel-announcements">
          <CustomerAnnouncementsSection customer={c} />
        </div>
        )}

        {/* Iter 400.8 — universal Log Activity modal (open from Timeline
            tab CTA; can be opened from other surfaces later too). */}
        <LogActivityModal
          open={logActivityOpen}
          onClose={() => setLogActivityOpen(false)}
          onLogged={load}
          customerId={id}
        />
      
    </div>
    </EntityShell>
  );
}

/* ---------------- Iter 418.w · Customer email safety-net toggle ---------------- */
function EmailNotificationsToggle({ customer, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const on = !!customer?.email_notifications_enabled;
  const last = customer?.last_email_sent_at;
  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.patch(`/customers/${customer.id}/account`, {
        email_notifications_enabled: !on,
      });
      toast.success(!on ? "Customer emails ON" : "Customer emails OFF");
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not update");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="inline-flex items-center gap-1" data-testid="customer-email-notif-wrap">
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        className={`hv-btn hv-btn-ghost ${on ? "text-emerald-300 border-emerald-500/40" : "text-amber-300 border-amber-500/40"}`}
        data-testid="customer-email-notif-toggle"
        title={
          on
            ? "Customer will receive automated emails for portal-facing events (JC, quote, SO, invoice, credit note, statement, document)"
            : "SendGrid emails are OFF for this customer — the safety-net default. Click to enable."
        }
      >
        <Mail className="w-4 h-4" />
        Emails: {on ? "ON" : "OFF"}
      </button>
      {/* Iter 418.y — send-preview button. Fires whichever template to the
          admin's own email so they can eyeball the brand + copy. */}
      <button
        type="button"
        onClick={() => setTestOpen(true)}
        className="hv-btn hv-btn-ghost text-xs"
        data-testid="customer-email-notif-test"
        title="Preview any of the 8 branded emails — sends to your own address"
      >
        <Send className="w-3 h-3" /> Test
      </button>
      {last && (
        <span
          className="text-[10px] text-[#647087] hidden md:inline-block"
          data-testid="customer-email-notif-last"
          title={`Last email dispatched at ${last}`}
        >
          · last sent {new Date(last).toLocaleDateString()} ({customer.last_email_sent_kind || "n/a"})
        </span>
      )}
      {testOpen && (
        <EmailPreviewMenu
          customerId={customer.id}
          onClose={() => setTestOpen(false)}
        />
      )}
    </div>
  );
}

function EmailPreviewMenu({ customerId, onClose }) {
  const kinds = [
    { k: "jc_new", l: "JC created" },
    { k: "jc_completed", l: "JC signed off" },
    { k: "quote_added", l: "Quote (CF)" },
    { k: "sales_order_added", l: "Sales order (CF)" },
    { k: "invoice_added", l: "Invoice" },
    { k: "credit_note_added", l: "Credit note" },
    { k: "statement_added", l: "Statement" },
    { k: "document_added", l: "Document uploaded" },
  ];
  const [sending, setSending] = useState(null);
  const send = async (kind) => {
    setSending(kind);
    try {
      const { data } = await api.post(`/customers/${customerId}/notify-test`, { kind });
      toast.success(`Preview sent to ${data.sent_to}`);
      onClose?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Preview send failed");
    } finally {
      setSending(null);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" role="dialog">
      <div className="hv-card w-full max-w-sm p-4" data-testid="email-preview-menu">
        <div className="flex items-center gap-2 mb-2">
          <Send className="w-4 h-4 text-cyan-300" />
          <h3 className="font-display text-base font-bold">Preview an email</h3>
        </div>
        <p className="text-xs text-[#647087] mb-3">
          Sends the branded template to your own address — the customer
          flag and per-user opt-out are bypassed so you can eyeball copy.
        </p>
        <div className="grid grid-cols-2 gap-2">
          {kinds.map((it) => (
            <button
              key={it.k}
              type="button"
              onClick={() => send(it.k)}
              disabled={sending !== null}
              className="hv-btn hv-btn-ghost text-xs justify-start"
              data-testid={`email-preview-${it.k}`}
            >
              {sending === it.k ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              {it.l}
            </button>
          ))}
        </div>
        <div className="flex justify-end mt-4 pt-3 border-t border-white/5">
          <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost text-xs">Close</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Sign in as portal owner ---------------- */
function PortalPreviewButton({ customerId, customerName }) {
  // [Iter 349.r — Dirk, 2026-02-27] Rebuilt from a single "Sign in as
  // portal owner" button into a dropdown-picker "Sign into portal as…"
  // that lists EVERY portal user under the customer (owner / admin /
  // accounts / maintenance / custom) so staff can validate exactly
  // what each user sees. Backed by `POST /customers/{id}/impersonate-
  // portal-owner` with an optional `target_user_id`. Every session is
  // audit-logged (kind=impersonate_portal_user, actor + target + preset).
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [portalUsers, setPortalUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const navigate = useNavigate();
  const { setUser, user: staffUser } = useAuth();

  // Fetch the portal-user roster the moment the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingUsers(true);
      try {
        const { data } = await api.get(`/customers/${customerId}/portal-users`);
        if (cancelled) return;
        const users = Array.isArray(data) ? data : (data?.users || []);
        // Sort: owner presets first, then admin, then everyone else.
        const rank = { owner: 0, admin: 1, accounts: 2, maintenance: 3 };
        users.sort((a, b) => (rank[a.portal_preset] ?? 9) - (rank[b.portal_preset] ?? 9));
        setPortalUsers(users);
        // Auto-select the first one (usually the owner) so the modal
        // is immediately actionable.
        if (users[0]?.id) setSelectedUserId(users[0].id);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Couldn't load portal users.");
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, customerId]);

  const enter = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const body = { password: pwd };
      // No portal users at all → fall through to the legacy behaviour
      // (auto-creates a placeholder with owner preset).
      if (selectedUserId) body.target_user_id = selectedUserId;
      const { data } = await api.post(
        `/customers/${customerId}/impersonate-portal-owner`,
        body,
      );
      const currentToken = localStorage.getItem("hv_token");
      if (currentToken && staffUser) {
        localStorage.setItem("hv_impersonator_token", currentToken);
        localStorage.setItem(
          "hv_impersonator_user",
          JSON.stringify({
            id: staffUser.id,
            name: staffUser.name,
            email: staffUser.email,
            role: staffUser.role,
            return_to: `/customers/${customerId}`,
            customer_name: customerName,
          }),
        );
      }
      localStorage.setItem("hv_token", data.token);
      setUser(data.user);
      window.dispatchEvent(new CustomEvent("hv:impersonation-changed"));
      navigate("/portal");
    } catch (e2) {
      setErr(
        e2?.response?.data?.detail
        || "Could not sign into the portal as that user.",
      );
    } finally {
      setBusy(false);
    }
  };

  const selected = portalUsers.find((u) => u.id === selectedUserId);
  const presetLabel = (u) => {
    const map = {
      owner: "Owner", admin: "Admin", accounts: "Accounts",
      maintenance: "Maintenance", custom: "Custom",
    };
    return map[u?.portal_preset] || u?.customer_role || "—";
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="hv-btn hv-btn-ghost"
        data-testid="customer-portal-preview-btn"
        title="Sign into this customer's portal as any of their users — every action is audit-logged"
      >
        <Eye className="w-4 h-4" /> Sign into portal
      </button>
      {open && (
        <Modal
          open
          onClose={() => {
            setOpen(false);
            setPwd("");
            setErr("");
            setSelectedUserId("");
          }}
          title="Sign into portal"
          testId="portal-preview-modal"
          maxWidth="max-w-md"
        >
          <form onSubmit={enter} className="space-y-3" data-testid="portal-preview-form">
            <div className="text-sm text-[#a0aabf]">
              Choose which portal user of{" "}
              <span className="text-[#1fb6c1] font-medium">{customerName}</span>{" "}
              you want to sign in as — you'll see exactly what they see.
              {" "}
              <span className="text-amber-300">Every action is audit-logged under your account.</span>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
                Portal user
              </label>
              {loadingUsers ? (
                <div className="text-xs text-[#a0aabf] mt-2">Loading portal users…</div>
              ) : portalUsers.length === 0 ? (
                <div className="text-xs text-[#a0aabf] mt-2">
                  No portal users yet — signing in will create a temporary
                  owner-preset preview user for this customer.
                </div>
              ) : (
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="hv-input mt-1 w-full"
                  data-testid="portal-preview-user-select"
                >
                  {portalUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {(u.name || u.email)}
                      {" — "}
                      {presetLabel(u)}
                      {u.email && u.name ? ` · ${u.email}` : ""}
                    </option>
                  ))}
                </select>
              )}
              {selected && (
                <div className="text-[10px] text-[#7d8aa3] mt-1">
                  Preset: <span className="text-[#c3ccdb]">{presetLabel(selected)}</span>
                  {" · "}
                  Role: <span className="text-[#c3ccdb]">{selected.customer_role || "—"}</span>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
                Confirm your password
              </label>
              <input
                type="password"
                autoFocus
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                className="hv-input mt-1"
                data-testid="portal-preview-pwd"
              />
            </div>
            {err && <div className="text-xs text-rose-300" data-testid="portal-preview-err">{err}</div>}
            <button
              type="submit"
              disabled={busy || !pwd || loadingUsers}
              className="hv-btn hv-btn-primary w-full"
              data-testid="portal-preview-submit"
            >
              <Eye className="w-4 h-4" />{" "}
              {busy
                ? "Signing in…"
                : selected
                  ? `Sign in as ${selected.name || selected.email}`
                  : "Sign in as portal preview"}
            </button>
          </form>
        </Modal>
      )}
    </>
  );
}

/* ---------------- Portal access — multi-user manager ---------------- */
function PortalInviteButton({ customer, onChanged }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [pwdInput, setPwdInput] = useState("");
  // Iter 427.p41 — unified preset picker (retires the standalone
  // `customer_role` dropdown). Backend derives customer_role.
  const [presetInput, setPresetInput] = useState("owner");
  // Preflight lookup so we hide the password field for identities
  // that already exist on another customer (one identity = one password).
  const [emailLookup, setEmailLookup] = useState(null);
  const [tempCreds, setTempCreds] = useState(null); // { email, password }
  const [resetForUid, setResetForUid] = useState(null);
  const [resetPwd, setResetPwd] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers/${customer.id}/portal-users`);
      setUsers(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not load portal users"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      refresh();
      setShowAdd(false);
      setTempCreds(null);
      setResetForUid(null);
      setResetPwd("");
      setErr("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const startAdd = () => {
    setShowAdd(true);
    setEmailInput(users.length === 0 ? customer.email || "" : "");
    setNameInput(users.length === 0 ? customer.name || "" : "");
    setPwdInput("");
    setErr("");
  };

  const preflightEmail = async () => {
    const email = (emailInput || "").trim().toLowerCase();
    if (!email || !/@/.test(email)) {
      setEmailLookup(null);
      return;
    }
    try {
      const { data } = await api.get(
        `/customers/${customer.id}/portal-user-lookup?email=${encodeURIComponent(email)}`,
      );
      setEmailLookup(data);
      if (data?.exists && data?.has_password) setPwdInput("");
    } catch (_) {
      setEmailLookup(null);
    }
  };

  const submitAdd = async () => {
    if (emailLookup?.exists && emailLookup?.already_on_this_customer) {
      setErr("This email is already a portal user for this customer.");
      return;
    }
    if (emailLookup?.exists && emailLookup?.is_staff) {
      setErr("This email belongs to a staff account and can't be added.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/customers/${customer.id}/invite-portal`, {
        email: emailInput,
        name: nameInput,
        // Iter 427.p41 — password never sent on cross-customer add.
        password: emailLookup?.exists ? undefined : (pwdInput || undefined),
        portal_preset: presetInput,
      });
      // `password_free: true` → backend stored no password hash. Client must
      // claim a password on first sign-in via the Login page's 409 flow.
      if (data.password_free || data.membership_added) {
        setTempCreds({
          email: data.user.email,
          password: null,
          passwordFree: true,
          crossCustomer: !!data.membership_added,
        });
      } else {
        setTempCreds({ email: data.user.email, password: data.temp_password });
      }
      setShowAdd(false);
      setEmailInput("");
      setNameInput("");
      setPwdInput("");
      setEmailLookup(null);
      await refresh();
      onChanged?.();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not create portal user"));
    } finally {
      setBusy(false);
    }
  };

  const startReset = (uid) => {
    setResetForUid(uid);
    setResetPwd("");
    setErr("");
  };

  const submitReset = async () => {
    if (!resetForUid) return;
    if (resetPwd.length < 4) {
      setErr("Password must be at least 4 characters.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post(`/users/${resetForUid}/set-password`, { new_password: resetPwd });
      const u = users.find((x) => x.id === resetForUid);
      setTempCreds({ email: u?.email, password: resetPwd });
      setResetForUid(null);
      setResetPwd("");
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not reset password"));
    } finally {
      setBusy(false);
    }
  };

  const removeUser = async (uid, email) => {
    if (!window.confirm(`Remove portal access for ${email}? This will delete their login.`)) return;
    setBusy(true);
    setErr("");
    try {
      await api.delete(`/users/${uid}`);
      await refresh();
      onChanged?.();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not remove user"));
    } finally {
      setBusy(false);
    }
  };

  const hasAny = users.length > 0;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="hv-btn hv-btn-ghost"
        data-testid="customer-portal-btn"
        title="Manage portal access for this customer"
      >
        <KeyRound className="w-4 h-4" />
        {hasAny ? `Portal access (${users.length || ""})` : "Invite to portal"}
      </button>
      {open && (
        <Modal
          open
          onClose={() => setOpen(false)}
          title="Customer portal access"
          testId="portal-invite-modal"
          maxWidth="max-w-xl"
        >
          <div className="space-y-3" data-testid="portal-users-panel">
            <div className="text-xs text-[#a0aabf] flex items-start gap-2">
              <Users className="w-4 h-4 mt-0.5 text-[#1fb6c1] shrink-0" />
              <span>
                Add as many portal logins as the customer needs — owners,
                managers, technical staff. Each user only sees this customer's
                jobs and tickets.
              </span>
            </div>

            {tempCreds && (
              <div className="hv-card p-3 space-y-2 border border-[#1fb6c1]/40 bg-[#1fb6c1]/5" data-testid="portal-temp-creds">
                {tempCreds.passwordFree ? (
                  <>
                    <div className="text-xs text-[#1fb6c1] uppercase tracking-wider font-bold">
                      Portal access created
                    </div>
                    <p className="text-xs text-[#a0aabf]">
                      Just share the app URL and this email with the customer.
                      On first sign-in, they'll be prompted to create their
                      own password — you don't need to send one.
                    </p>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider text-[#647087]">Email</div>
                        <div className="font-mono text-sm truncate">{tempCreds.email}</div>
                      </div>
                      <button
                        onClick={() => navigator.clipboard.writeText(tempCreds.email || "")}
                        className="hv-btn hv-btn-ghost text-xs"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider text-[#647087]">App URL</div>
                        <div className="font-mono text-sm truncate">{window.location.origin}</div>
                      </div>
                      <button
                        onClick={() => navigator.clipboard.writeText(window.location.origin)}
                        className="hv-btn hv-btn-ghost text-xs"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-xs text-[#1fb6c1] uppercase tracking-wider font-bold">
                      Share these credentials securely
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider text-[#647087]">Email</div>
                        <div className="font-mono text-sm truncate">{tempCreds.email}</div>
                      </div>
                      <button
                        onClick={() => navigator.clipboard.writeText(tempCreds.email || "")}
                        className="hv-btn hv-btn-ghost text-xs"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider text-[#647087]">Password</div>
                        <div className="font-mono text-sm truncate" data-testid="portal-temp-pwd">
                          {tempCreds.password}
                        </div>
                      </div>
                      <button
                        onClick={() => navigator.clipboard.writeText(tempCreds.password || "")}
                        className="hv-btn hv-btn-ghost text-xs"
                        data-testid="portal-copy-pwd"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </>
                )}
                <button
                  onClick={() => setTempCreds(null)}
                  className="text-[10px] uppercase tracking-wider text-[#647087] hover:text-white"
                >
                  Dismiss
                </button>
              </div>
            )}

            {err && <div className="text-xs text-rose-300" data-testid="portal-err">{err}</div>}

            {/* Existing users list */}
            <div className="space-y-2" data-testid="portal-users-list">
              {loading ? (
                <div className="text-xs text-[#647087]">Loading…</div>
              ) : users.length === 0 ? (
                <div className="text-xs text-[#647087] italic">
                  No portal users yet. Add one below.
                </div>
              ) : (
                users.map((u) => (
                  <div
                    key={u.id}
                    className="hv-card p-3 flex items-center gap-3 flex-wrap"
                    data-testid={`portal-user-${u.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{u.name}</div>
                      <div className="text-xs text-[#647087] font-mono truncate">{u.email}</div>
                      {customer.portal_user_id === u.id && (
                        <span className="inline-block mt-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[#1fb6c1]/40 text-[#1fb6c1] bg-[#1fb6c1]/5">
                          Primary
                        </span>
                      )}
                    </div>
                    {resetForUid === u.id ? (
                      <div className="flex items-center gap-2 w-full sm:w-auto">
                        <input
                          autoFocus
                          type="text"
                          placeholder="New password"
                          value={resetPwd}
                          onChange={(e) => setResetPwd(e.target.value)}
                          className="hv-input text-sm flex-1 sm:w-44"
                          data-testid={`portal-reset-input-${u.id}`}
                        />
                        <button
                          onClick={submitReset}
                          disabled={busy}
                          className="hv-btn hv-btn-primary text-xs"
                          data-testid={`portal-reset-confirm-${u.id}`}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setResetForUid(null)}
                          className="hv-btn hv-btn-ghost text-xs"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => startReset(u.id)}
                          className="hv-btn hv-btn-ghost text-xs"
                          title="Set a new password"
                          data-testid={`portal-reset-${u.id}`}
                        >
                          <KeyRound className="w-3 h-3" /> Reset
                        </button>
                        <button
                          onClick={() => removeUser(u.id, u.email)}
                          disabled={busy}
                          className="hv-btn hv-btn-ghost text-xs text-rose-300 hover:!text-rose-200"
                          title="Remove portal access"
                          data-testid={`portal-remove-${u.id}`}
                        >
                          <Trash2 className="w-3 h-3" /> Remove
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Add user form */}
            {showAdd ? (
              <div className="hv-card p-3 space-y-2 border border-white/10" data-testid="portal-add-form">
                <div className="text-xs uppercase tracking-wider text-[#1fb6c1] font-bold">
                  Add portal user
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Email *</label>
                  <input
                    type="email"
                    required
                    value={emailInput}
                    onChange={(e) => { setEmailInput(e.target.value); setEmailLookup(null); }}
                    onBlur={preflightEmail}
                    className="hv-input mt-1"
                    placeholder="staff@customer.com"
                    data-testid="portal-add-email"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Display name</label>
                  <input
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    className="hv-input mt-1"
                    placeholder="Optional"
                    data-testid="portal-add-name"
                  />
                </div>
                {emailLookup?.exists && emailLookup?.already_on_this_customer && (
                  <div className="text-xs px-3 py-2 rounded border border-rose-500/40 bg-rose-500/10 text-rose-200" data-testid="portal-add-already-member">
                    This email is already a portal user for this customer.
                  </div>
                )}
                {emailLookup?.exists && emailLookup?.is_staff && (
                  <div className="text-xs px-3 py-2 rounded border border-rose-500/40 bg-rose-500/10 text-rose-200" data-testid="portal-add-staff-email">
                    This email belongs to a staff account and can&rsquo;t be added.
                  </div>
                )}
                {emailLookup?.exists && !emailLookup?.already_on_this_customer && !emailLookup?.is_staff && (
                  <div className="text-xs px-3 py-2 rounded border border-[#1fb6c1]/40 bg-[#1fb6c1]/10 text-[#a0e8ee]" data-testid="portal-add-existing-identity">
                    This email already has a Hidroverse account{emailLookup.name ? ` (${emailLookup.name})` : ""}. They&rsquo;ll use their existing password and pick this customer at the login screen. A single identity has ONE password across every customer.
                  </div>
                )}
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Portal role *</label>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 mt-1" data-testid="portal-add-preset">
                    {[
                      ["owner",       "Owner",       "Full access, invites team"],
                      ["admin",       "Admin",       "Full except team-manage"],
                      ["accounts",    "Accounts",    "Quotes / invoices / docs"],
                      ["maintenance", "Maintenance", "Jobs, tickets, ordering"],
                      ["custom",      "Custom",      "Fine-grained per box"],
                    ].map(([k, label, desc]) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setPresetInput(k)}
                        className={`text-left p-2 rounded border transition ${presetInput === k ? "border-[#1fb6c1] bg-[#1fb6c1]/10" : "border-white/10 hover:border-white/20"}`}
                        data-testid={`portal-add-preset-${k}`}
                      >
                        <div className="text-xs font-bold uppercase tracking-wider">{label}</div>
                        <div className="text-[10px] text-[#a0aabf] mt-0.5 leading-tight">{desc}</div>
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-[#647087] mt-1">
                    Preset drives permissions AND email fan-out — pick the
                    tightest fit; the user's access can be changed later.
                  </p>
                </div>
                {!(emailLookup?.exists && emailLookup?.has_password) && (
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">
                      Password (optional)
                    </label>
                    <input
                      type="text"
                      value={pwdInput}
                      onChange={(e) => setPwdInput(e.target.value)}
                      className="hv-input mt-1 font-mono"
                      placeholder="Leave blank — they'll create on first sign-in"
                      data-testid="portal-add-pwd"
                    />
                    <p className="text-[10px] text-[#647087] mt-1">
                      Recommended: leave blank. Just share the app URL and
                      email — the customer will set their own password when they
                      first sign in.
                    </p>
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={submitAdd}
                    disabled={busy || !emailInput}
                    className="hv-btn hv-btn-primary flex-1"
                    data-testid="portal-add-submit"
                  >
                    <KeyRound className="w-4 h-4" /> {busy ? "Creating…" : "Create access"}
                  </button>
                  <button
                    onClick={() => setShowAdd(false)}
                    className="hv-btn hv-btn-ghost"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={startAdd}
                className="hv-btn hv-btn-ghost w-full justify-center"
                data-testid="portal-add-btn"
              >
                <Plus className="w-4 h-4" /> Add portal user
              </button>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}


/* ─── Iter 400.21 — Customer → Performance sub-tabs ─────────────────
   Mirrors the top-level /performance page tabs (Logbook / Analytics /
   Plant Uptime / Approvals) but scoped to this one customer so owners
   can approve edit requests from within the customer profile. */
function CustomerPerformanceTabs({ customerId, canEdit }) {
  const [sub, setSub] = useState("logbook");
  const [pendingCount, setPendingCount] = useState(0);
  useEffect(() => {
    let alive = true;
    api
      .get(`/perf/readings/edit-requests?status=pending&customer_id=${customerId}`)
      .then(({ data }) => { if (alive) setPendingCount(data?.count || 0); })
      .catch(() => {});
    return () => { alive = false; };
  }, [customerId, sub]);
  return (
    <div className="space-y-3">
      <div
        className="flex items-center gap-1 bg-white/[0.02] border border-white/10 rounded-md p-1 w-fit"
        data-testid="customer-perf-subtabs"
      >
        {[
          { k: "logbook", label: "Logbook", cls: "text-[#22d3ee] bg-[#22d3ee]/20" },
          { k: "analytics", label: "Analytics", cls: "text-[#A78BFA] bg-[#A78BFA]/20" },
          { k: "sla", label: "Plant Uptime", cls: "text-[#22d3ee] bg-[#22d3ee]/20" },
          { k: "approvals", label: "Approvals", cls: "text-amber-300 bg-amber-500/20" },
        ].map((t) => (
          <button
            key={t.k}
            type="button"
            onClick={() => setSub(t.k)}
            className={`px-3 py-1.5 rounded text-xs font-medium transition inline-flex items-center gap-1.5 ${sub === t.k ? t.cls : "text-[#a0aabf] hover:text-white"}`}
            data-testid={`customer-perf-subtab-${t.k}`}
          >
            {t.label}
            {t.k === "approvals" && pendingCount > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 rounded-full text-[9px] font-bold px-1 bg-amber-500 text-black">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>
      {sub === "logbook" && (
        <PerformanceBlock customerId={customerId} canEdit={canEdit} canCapture defaultOpen visibleSection="logbook" />
      )}
      {sub === "analytics" && (
        <PerformanceBlock customerId={customerId} canEdit={canEdit} canCapture defaultOpen visibleSection="analytics" />
      )}
      {sub === "sla" && (
        <PlantUptimeBlock customerId={customerId} defaultOpen />
      )}
      {sub === "approvals" && (
        <PerfEditRequestsInbox customerId={customerId} />
      )}
    </div>
  );
}
